import { useState, useEffect } from 'react';
import styles from './KrpanoCompass.module.css';

/**
 * KrpanoCompass — React replacement cho compass layer trong krpano.
 *
 * Cơ chế:
 *  - Đăng ký global `window.onKrpanoCompassUpdate` khi mount.
 *  - krpano gọi callback này mỗi khi view thay đổi (onviewchange → jscall).
 *  - Callback đọc `view.hlookat` + `view.camroll` từ krpanoRef rồi tính góc xoay.
 *
 * Công thức (giống krpano action gốc):
 *   plateRotation = globalHeading - (hlookat + camroll)
 *
 * @param {React.RefObject} krpanoRef    - ref trỏ tới krpano interface
 * @param {string}  imagesBase           - URL prefix tới thư mục ảnh compass
 *                                         VD: "/360/tongquan/images"
 * @param {number}  [globalHeading=210]  - Hướng bắc của panorama (degree)
 * @param {boolean} [visible=true]       - Ẩn/hiện compass
 */
function KrpanoCompass({
  krpanoRef,
  imagesBase,
  globalHeading = 210,
  visible = true,
}) {
  const [rotation, setRotation] = useState(globalHeading);

  useEffect(() => {
    const update = () => {
      const krpano = krpanoRef?.current;
      if (!krpano) return;

      const hlookat  = parseFloat(krpano.get('view.hlookat'))  || 0;
      const camroll  = parseFloat(krpano.get('view.camroll'))   || 0;
      const fov      = parseFloat(krpano.get('view.fov'))       || 120;
      setRotation(globalHeading - (hlookat + camroll));
      window.dispatchEvent(new CustomEvent('krpanoViewChange', { detail: { hlookat, fov } }));
    };

    // Đăng ký callback global để krpano gọi qua jscall()
    window.onKrpanoCompassUpdate = update;

    return () => {
      if (window.onKrpanoCompassUpdate === update) {
        window.onKrpanoCompassUpdate = null;
      }
    };
  }, [krpanoRef, globalHeading]);

  if (!visible) return null;

  return (
    <div className={styles.compass} aria-hidden="true">
      {/* Background vòng tròn */}
      <img
        src={`${imagesBase}/compass_bg.png`}
        className={styles.bg}
        alt=""
        draggable={false}
      />
      {/* Mặt đĩa xoay — chứa N/S/E/W */}
      <img
        src={`${imagesBase}/compass_plate.png`}
        className={styles.plate}
        style={{ transform: `rotate(${rotation}deg)` }}
        alt="Compass"
        draggable={false}
      />
      {/* Kim chỉ hướng — cố định, luôn chỉ lên */}
      <img
        src={`${imagesBase}/compass_pointer.png`}
        className={styles.pointer}
        alt=""
        draggable={false}
      />
    </div>
  );
}

export default KrpanoCompass;
